import { defineStore } from "pinia";
import { ref, type Ref } from "vue";

import {
  getAdminApplications,
  getAdminCommercial,
  getAdminOverview,
  getAdminSupport,
  getAdminUsers,
} from "@/api/admin";
import { ApiError, type AsyncStatus } from "@/types/api";
import type {
  AdminApplication,
  AdminCommercialItem,
  AdminListQuery,
  AdminOverview,
  AdminPage,
  AdminSupportRequest,
  AdminUser,
} from "@/types/admin";

interface ResourceState<T> {
  data: Ref<T | null>;
  status: Ref<AsyncStatus>;
  error: Ref<ApiError | null>;
  generation: Ref<number>;
  refreshing: Ref<boolean>;
  controller: AbortController | null;
}

function createResource<T>(): ResourceState<T> {
  return {
    data: ref<T | null>(null) as Ref<T | null>,
    status: ref<AsyncStatus>("idle"),
    error: ref<ApiError | null>(null),
    generation: ref(0),
    refreshing: ref(false),
    controller: null,
  };
}

function normalizeError(caught: unknown, fallback: string): ApiError {
  return caught instanceof ApiError ? caught : new ApiError(fallback);
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException
    ? caught.name === "AbortError"
    : caught instanceof Error && caught.name === "AbortError";
}

function isEmptyPage<T>(value: AdminPage<T>): boolean {
  return value.items.length === 0;
}

export const useAdminStore = defineStore("admin", () => {
  const overviewResource = createResource<AdminOverview>();
  const usersResource = createResource<AdminPage<AdminUser>>();
  const applicationsResource = createResource<AdminPage<AdminApplication>>();
  const supportResource = createResource<AdminPage<AdminSupportRequest>>();
  const commercialResource = createResource<AdminPage<AdminCommercialItem>>();
  const ownerId = ref<string | null>(null);
  let sessionGeneration = 0;
  let ownerBinding: { id: string | null; session: number } | null = null;

  function isCurrent<T>(
    resource: ResourceState<T>,
    controller: AbortController,
    generation: number,
    session: number,
  ): boolean {
    return resource.controller === controller
      && resource.generation.value === generation
      && sessionGeneration === session;
  }

  function owns<T>(resource: ResourceState<T>, controller: AbortController, generation: number, session: number): boolean {
    return isCurrent(resource, controller, generation, session) && !controller.signal.aborted;
  }

  async function run<T>(
    resource: ResourceState<T>,
    request: (signal: AbortSignal) => Promise<T>,
    empty: (value: T) => boolean,
    callerSignal?: AbortSignal,
    fallback = "Administration data could not be loaded.",
  ): Promise<T | null> {
    resource.controller?.abort();
    const controller = new AbortController();
    resource.controller = controller;
    const generation = resource.generation.value + 1;
    resource.generation.value = generation;
    const session = sessionGeneration;
    const hadData = resource.data.value !== null;
    const previousStatus = resource.status.value;
    resource.error.value = null;
    resource.refreshing.value = hadData;
    if (!hadData) resource.status.value = "loading";

    let callerAbortListener: (() => void) | undefined;
    if (callerSignal) {
      callerAbortListener = () => controller.abort();
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener("abort", callerAbortListener, { once: true });
    }

    try {
      const value = await request(controller.signal);
      if (!owns(resource, controller, generation, session)) {
        return sessionGeneration === session ? resource.data.value : null;
      }
      resource.data.value = value;
      resource.status.value = empty(value) ? "empty" : "success";
      resource.error.value = null;
      return value;
    } catch (caught: unknown) {
      if (!owns(resource, controller, generation, session)) {
        return sessionGeneration === session ? resource.data.value : null;
      }
      if (controller.signal.aborted || isAbortError(caught)) return resource.data.value;
      const normalized = normalizeError(caught, fallback);
      resource.error.value = normalized;
      if (!hadData) resource.status.value = "error";
      throw normalized;
    } finally {
      if (callerSignal && callerAbortListener) {
        callerSignal.removeEventListener("abort", callerAbortListener);
      }
      if (isCurrent(resource, controller, generation, session)) {
        resource.refreshing.value = false;
        if (controller.signal.aborted && !hadData && resource.status.value === "loading") {
          resource.status.value = previousStatus;
        }
        resource.controller = null;
      }
    }
  }

  function invalidate<T>(resource: ResourceState<T>): void {
    resource.generation.value += 1;
    resource.controller?.abort();
    resource.controller = null;
    resource.data.value = null;
    resource.status.value = "idle";
    resource.error.value = null;
    resource.refreshing.value = false;
  }

  async function loadOverview(signal?: AbortSignal): Promise<AdminOverview | null> {
    return run(
      overviewResource,
      (requestSignal) => getAdminOverview(requestSignal),
      () => false,
      signal,
      "Administration overview could not be loaded.",
    );
  }

  async function loadUsers(
    query: AdminListQuery = {},
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminUser> | null> {
    return run(
      usersResource,
      (requestSignal) => getAdminUsers(query, requestSignal),
      isEmptyPage,
      signal,
      "Administration users could not be loaded.",
    );
  }

  async function loadApplications(
    query: AdminListQuery = {},
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminApplication> | null> {
    return run(
      applicationsResource,
      (requestSignal) => getAdminApplications(query, requestSignal),
      isEmptyPage,
      signal,
      "Administration applications could not be loaded.",
    );
  }

  async function loadSupport(
    query: AdminListQuery = {},
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminSupportRequest> | null> {
    return run(
      supportResource,
      (requestSignal) => getAdminSupport(query, requestSignal),
      isEmptyPage,
      signal,
      "Administration support could not be loaded.",
    );
  }

  async function loadCommercial(
    query: AdminListQuery = {},
    signal?: AbortSignal,
  ): Promise<AdminPage<AdminCommercialItem> | null> {
    return run(
      commercialResource,
      (requestSignal) => getAdminCommercial(query, requestSignal),
      isEmptyPage,
      signal,
      "Administration commercial data could not be loaded.",
    );
  }

  function reset(): void {
    sessionGeneration += 1;
    invalidate(overviewResource);
    invalidate(usersResource);
    invalidate(applicationsResource);
    invalidate(supportResource);
    invalidate(commercialResource);
    ownerId.value = null;
    ownerBinding = null;
  }

  function setOwner(nextOwnerId: string | null): void {
    if (ownerBinding?.session === sessionGeneration && ownerBinding.id === nextOwnerId) return;
    reset();
    ownerId.value = nextOwnerId;
    ownerBinding = { id: nextOwnerId, session: sessionGeneration };
  }

  return {
    applications: applicationsResource.data,
    applicationsData: applicationsResource.data,
    applicationsError: applicationsResource.error,
    applicationsGeneration: applicationsResource.generation,
    applicationsRefreshing: applicationsResource.refreshing,
    applicationsStatus: applicationsResource.status,
    commercial: commercialResource.data,
    commercialData: commercialResource.data,
    commercialError: commercialResource.error,
    commercialGeneration: commercialResource.generation,
    commercialRefreshing: commercialResource.refreshing,
    commercialStatus: commercialResource.status,
    isApplicationsRefreshing: applicationsResource.refreshing,
    isCommercialRefreshing: commercialResource.refreshing,
    isOverviewRefreshing: overviewResource.refreshing,
    isSupportRefreshing: supportResource.refreshing,
    isUsersRefreshing: usersResource.refreshing,
    loadApplications,
    loadCommercial,
    loadOverview,
    loadSupport,
    loadUsers,
    overview: overviewResource.data,
    overviewData: overviewResource.data,
    overviewError: overviewResource.error,
    overviewGeneration: overviewResource.generation,
    overviewRefreshing: overviewResource.refreshing,
    overviewStatus: overviewResource.status,
    ownerId,
    reset,
    setOwner,
    support: supportResource.data,
    supportData: supportResource.data,
    supportError: supportResource.error,
    supportGeneration: supportResource.generation,
    supportRefreshing: supportResource.refreshing,
    supportStatus: supportResource.status,
    users: usersResource.data,
    usersData: usersResource.data,
    usersError: usersResource.error,
    usersGeneration: usersResource.generation,
    usersRefreshing: usersResource.refreshing,
    usersStatus: usersResource.status,
  };
});
